import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useUpdateBewerber } from '../../api/bewerber';
import { useSendBewerberEmail, usePreviewBewerberEmail } from '../../api/email';
import { EmailEditor } from '../email/EmailEditor';
import type { Bewerber } from '../../types/bewerber';
import { BewerberStatus } from '../../types/bewerber';

interface AnfordernModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerber: Bewerber;
  onSuccess?: () => void;
}

interface FormValues {
  ortStelle: boolean;
  lebenslauf: boolean;
}

type Step = 'options' | 'editor';

export function AnfordernModal({
  isOpen,
  onClose,
  bewerber,
  onSuccess,
}: AnfordernModalProps) {
  const sendEmailMutation = useSendBewerberEmail();
  const updateBewerberMutation = useUpdateBewerber();
  const previewMutation = usePreviewBewerberEmail();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('options');
  const [editedHtml, setEditedHtml] = useState('');
  const [editedSubject, setEditedSubject] = useState('');

  const {
    register,
    reset,
    watch,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      ortStelle: false,
      lebenslauf: false,
    },
  });

  const ortStelle = watch('ortStelle');
  const lebenslauf = watch('lebenslauf');

  // Validation: at least one checkbox required
  const isValid = ortStelle || lebenslauf;

  const isPending = sendEmailMutation.isPending || updateBewerberMutation.isPending;

  const handleWeiter = async () => {
    const data = getValues();
    if (!isValid) {
      setError('Bitte wählen Sie mindestens eine Option aus.');
      return;
    }

    if (!bewerber.email) {
      setError('Bewerber hat keine E-Mail-Adresse.');
      return;
    }

    setError(null);
    try {
      const result = await previewMutation.mutateAsync({
        bewerberId: bewerber.id,
        data: {
          templateName: 'bewerber-anfordern',
          data: {
            ortStelle: data.ortStelle,
            lebenslauf: data.lebenslauf,
          },
        },
      });
      setEditedHtml(result.data.html);
      setEditedSubject(result.data.subject);
      setStep('editor');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Fehler beim Laden der Vorschau';
      setError(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join(', ') : String(msg));
    }
  };

  const handleSend = async () => {
    const data = getValues();
    setError(null);
    try {
      await sendEmailMutation.mutateAsync({
        bewerberId: bewerber.id,
        data: {
          templateName: 'bewerber-anfordern',
          data: {
            ortStelle: data.ortStelle,
            lebenslauf: data.lebenslauf,
          },
          bodyHtml: editedHtml,
          subject: editedSubject,
        },
      });

      // Update status to KEINE_ANLAGEN
      await updateBewerberMutation.mutateAsync({
        id: bewerber.id,
        data: { status: BewerberStatus.KEINE_ANLAGEN },
      });

      toast.success('Anforderung wurde versendet');
      handleClose();
      onSuccess?.();
    } catch (err: any) {
      let errorMessage = 'Fehler beim Versenden der Anforderung';
      const responseMessage = err?.response?.data?.message;
      if (Array.isArray(responseMessage)) {
        errorMessage = responseMessage.join(', ');
      } else if (typeof responseMessage === 'string') {
        errorMessage = responseMessage;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setStep('options');
    setEditedHtml('');
    setEditedSubject('');
    onClose();
  };

  if (!isOpen) return null;

  const bewerberName = `${bewerber.firstName} ${bewerber.lastName}`;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className={`relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full ${step === 'editor' ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Unterlagen anfordern
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {step === 'options'
                    ? <>Welche Informationen sollen von <span className="font-medium">{bewerberName}</span> angefordert werden?</>
                    : <>E-Mail an <span className="font-medium">{bewerberName}</span> bearbeiten und senden</>
                  }
                </p>

                {error && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {step === 'options' && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bitte auswählen (mindestens eine Option)
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            {...register('lebenslauf')}
                            className="h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Lebenslauf</span>
                            <p className="text-sm text-gray-500">Aktueller Lebenslauf als PDF</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            {...register('ortStelle')}
                            className="h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Ort/Stelle</span>
                            <p className="text-sm text-gray-500">Gewünschter Einsatzort oder bevorzugte Stelle</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {step === 'editor' && (
                  <div className="mt-4">
                    <EmailEditor
                      renderedHtml={editedHtml}
                      renderedSubject={editedSubject}
                      onHtmlChange={setEditedHtml}
                      onSubjectChange={setEditedSubject}
                      isLoading={previewMutation.isPending}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {step === 'options' && (
              <>
                <button
                  type="button"
                  onClick={handleWeiter}
                  disabled={previewMutation.isPending || !isValid}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {previewMutation.isPending ? 'Wird geladen...' : 'Weiter'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Abbrechen
                </button>
              </>
            )}
            {step === 'editor' && (
              <>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isPending ? 'Wird versendet...' : 'Senden'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('options'); setError(null); }}
                  disabled={isPending}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Zurück
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
